/**
 * Merkle Tree Worker Service — Transparent worker offload with main-thread fallback
 * 
 * Wraps merkle tree construction behind a unified async API.
 * If a Web Worker is available, SHA-256 hashing runs off the main thread,
 * keeping the UI responsive during large file preparation.
 * If the worker fails to load, falls back to the existing main-thread
 * constructMerkleTree — zero behavior change, same results.
 * 
 * Usage:
 *   import { merkleTreeWorkerService } from "@/services/merkle-tree-worker-service";
 *   await merkleTreeWorkerService.init();
 *   const tree = await merkleTreeWorkerService.buildTree(file, chunkSize, onProgress);
 */

import { constructMerkleTree, hash, hashBlob } from '@/lib/file';

// ============================================================
// Types
// ============================================================

type PendingRequest = {
  resolve: (value: any) => void;
  reject: (error: Error) => void;
};

export type MerkleProgressCallback = (progress: {
  phase: 'hashing' | 'building' | 'complete';
  progress: number;         // 0-100
  chunksProcessed: number;
  totalChunks: number;
}) => void;

// ============================================================
// Service Implementation
// ============================================================

class MerkleTreeWorkerService {
  private worker: Worker | null = null;
  private workerAvailable = false;
  private pendingRequests = new Map<number, PendingRequest>();
  private nextRequestId = 0;
  private initPromise: Promise<void> | null = null;

  // Progress callbacks keyed by buildId
  private progressCallbacks = new Map<string, MerkleProgressCallback>();

  // ============================================================
  // Initialization
  // ============================================================

  /**
   * Initialize the service. Safe to call multiple times — subsequent calls
   * return the same promise. Spawns the worker.
   */
  async init(): Promise<void> {
    if (this.initPromise) return this.initPromise;
    this.initPromise = this._doInit();
    return this.initPromise;
  }

  private async _doInit(): Promise<void> {
    try {
      await this.spawnWorker();
    } catch (e) {
      console.warn('[MerkleTreeWorkerService] Worker spawn failed, using main-thread fallback', e);
    }

    console.log(`[MerkleTreeWorkerService] Initialized — worker: ${this.workerAvailable ? 'YES' : 'NO (main-thread fallback)'}`);
  }

  private async spawnWorker(): Promise<void> {
    if (typeof Worker === 'undefined') {
      throw new Error('Web Workers not supported');
    }

    return new Promise<void>((resolve, reject) => {
      // P0-I Phase 1S root-cause fix: the previous 5 s init timeout was
      // too aggressive on resource-constrained peers (Kali Firefox on
      // Pi4-class hardware).  The worker itself spawns fine, but its
      // init-response is queued behind the cold-start activity burst
      // (peer connection, ICE gathering, Dexie subscriptions, WASM
      // init).  When the timeout fired we silently fell back to
      // main-thread merkle work — exactly what this service exists to
      // avoid — saturating the scheduler and starving React's Suspense
      // retry for minutes (254 s observed 4/26).  30 s timeout + 5 s
      // progress logging lets us distinguish "slow init that still
      // worked" from "genuine worker failure" without giving up early.
      const INIT_TIMEOUT_MS = 30000;
      const start = performance.now();
      const progressInterval = setInterval(() => {
        const elapsed = Math.round((performance.now() - start) / 1000);
        console.warn(
          `[MerkleTreeWorkerService] Worker init still pending after ${elapsed}s (will timeout at 30s) — main thread likely saturated`,
        );
      }, 5000);
      const timeout = setTimeout(() => {
        clearInterval(progressInterval);
        reject(new Error(`Worker init timed out after ${INIT_TIMEOUT_MS / 1000}s`));
      }, INIT_TIMEOUT_MS);

      try {
        // Worker is built as a separate entry point by build.ts → /merkle-tree-worker.js
        this.worker = new Worker('/merkle-tree-worker.js', { type: 'module' });

        this.worker.onmessage = (e: MessageEvent) => {
          this.handleMessage(e);
        };

        this.worker.onerror = (e: ErrorEvent) => {
          console.warn('[MerkleTreeWorkerService] Worker error:', e.message);
          clearInterval(progressInterval);
          clearTimeout(timeout);
          this.teardownWorker();
          reject(new Error(e.message));
        };

        // Send init command
        const initId = this.nextRequestId++;
        this.pendingRequests.set(initId, {
          resolve: () => {
            clearInterval(progressInterval);
            clearTimeout(timeout);
            this.workerAvailable = true;
            const elapsed = Math.round((performance.now() - start) / 1000);
            if (elapsed >= 5) {
              console.log(
                `[MerkleTreeWorkerService] Worker init completed after ${elapsed}s (slow but successful)`,
              );
            }
            resolve();
          },
          reject: (err) => {
            clearInterval(progressInterval);
            clearTimeout(timeout);
            this.teardownWorker();
            reject(err);
          }
        });

        this.worker.postMessage({ task: 'init', id: initId });
      } catch (e) {
        clearInterval(progressInterval);
        clearTimeout(timeout);
        reject(e);
      }
    });
  }

  private teardownWorker(): void {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
    this.workerAvailable = false;
    for (const [, pending] of this.pendingRequests) {
      pending.reject(new Error('Worker terminated'));
    }
    this.pendingRequests.clear();
    this.progressCallbacks.clear();
  }

  // ============================================================
  // Worker Communication
  // ============================================================

  private handleMessage(e: MessageEvent): void {
    const data = e.data;

    // Handle progress updates (no request ID resolution — they're fire-and-forget)
    if (data.type === 'progress') {
      const callback = this.progressCallbacks.get(data.buildId);
      if (callback) {
        callback({
          phase: data.phase,
          progress: data.progress,
          chunksProcessed: data.chunksProcessed,
          totalChunks: data.totalChunks
        });
      }
      return;
    }

    // Standard request/response resolution
    const { id, error, ...result } = data;
    const pending = this.pendingRequests.get(id);
    if (!pending) return;
    this.pendingRequests.delete(id);

    if (error) {
      pending.reject(new Error(error));
    } else {
      pending.resolve(result);
    }
  }

  private sendToWorker(task: string, data: Record<string, any>, transferables?: Transferable[]): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!this.worker || !this.workerAvailable) {
        reject(new Error('Worker not available'));
        return;
      }

      const id = this.nextRequestId++;
      this.pendingRequests.set(id, { resolve, reject });

      const message = { task, id, ...data };
      this.worker.postMessage(message, transferables || []);
    });
  }

  // ============================================================
  // Public API
  // ============================================================

  /**
   * Build a complete Merkle tree from a File.
   * Routes to worker if available, otherwise builds on main thread.
   * 
   * @param file - The file to build the tree for
   * @param chunkSize - Size of each chunk for leaf hashing
   * @param onProgress - Optional progress callback (0-100%)
   * @returns The Merkle tree as array of levels [leaves, ..., root]
   */
  async buildTree(
    file: File,
    chunkSize: number,
    onProgress?: (progress: number) => void
  ): Promise<Uint8Array[][]> {
    if (this.workerAvailable) {
      try {
        return await this.buildTreeViaWorker(file, chunkSize, onProgress);
      } catch (e) {
        console.warn('[MerkleTreeWorkerService] Worker buildTree failed, falling back to main thread', e);
        // Fall through to main-thread path
      }
    }

    // Main-thread fallback — existing constructMerkleTree
    return constructMerkleTree(file, chunkSize, onProgress);
  }

  private async buildTreeViaWorker(
    file: File,
    chunkSize: number,
    onProgress?: (progress: number) => void
  ): Promise<Uint8Array[][]> {
    const buildId = `tree_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

    // Register progress callback
    if (onProgress) {
      this.progressCallbacks.set(buildId, (p) => {
        onProgress(p.progress);
      });
    }

    try {
      const result = await this.sendToWorker('build-tree', {
        file,
        chunkSize,
        buildId
      });

      if (result.cancelled) {
        throw new Error('Tree build was cancelled');
      }

      // Deserialize: worker sends flat Uint8Arrays per level + levelSizes
      const tree = this.deserializeTree(result.levels, result.levelSizes);

      console.log(`[MerkleTreeWorkerService] Tree built via worker`, {
        levels: tree.length,
        totalChunks: result.totalChunks,
        rootHash: Array.from(result.rootHash.slice(0, 4) as Uint8Array)
          .map((b) => (b as number).toString(16).padStart(2, '0'))
          .join('')
      });

      return tree;
    } finally {
      this.progressCallbacks.delete(buildId);
    }
  }

  /**
   * Deserialize flat level buffers back to Uint8Array[][] tree format.
   * Each level is a flat buffer of concatenated 32-byte hashes.
   */
  private deserializeTree(levels: Uint8Array[], levelSizes: number[]): Uint8Array[][] {
    const tree: Uint8Array[][] = [];

    for (let i = 0; i < levels.length; i++) {
      const flat = levels[i];
      const count = levelSizes[i];
      const level: Uint8Array[] = [];

      for (let j = 0; j < count; j++) {
        level.push(new Uint8Array(flat.buffer, flat.byteOffset + j * 32, 32));
      }

      tree.push(level);
    }

    return tree;
  }

  /**
   * Cancel an in-progress tree build.
   */
  async cancelBuild(buildId: string): Promise<void> {
    if (!this.workerAvailable) return;

    try {
      await this.sendToWorker('cancel', { buildId });
    } catch {
      // Silent — build may already be complete
    }
    this.progressCallbacks.delete(buildId);
  }

  /**
   * Hash a single chunk via worker, or fallback to main thread.
   * Useful for individual chunk verification.
   */
  async hashChunk(chunk: ArrayBuffer): Promise<Uint8Array> {
    if (this.workerAvailable) {
      try {
        const result = await this.sendToWorker('hash-chunk', { chunk }, [chunk]);
        return result.hash as Uint8Array;
      } catch {
        // Fall through
      }
    }

    return hash(chunk);
  }

  /**
   * Hash a Blob via worker, or fallback to main thread.
   * Useful for root hash calculation of large files.
   */
  async hashFile(blob: Blob): Promise<Uint8Array> {
    if (this.workerAvailable) {
      try {
        const result = await this.sendToWorker('hash-blob', { blob });
        return result.hash as Uint8Array;
      } catch {
        // Fall through
      }
    }

    return hashBlob(blob);
  }

  // ============================================================
  // Status
  // ============================================================

  /** Whether the worker is active and hashing off the main thread */
  get isWorkerActive(): boolean {
    return this.workerAvailable;
  }

  /** Tear down the worker. Call on unmount if needed. */
  destroy(): void {
    this.teardownWorker();
    this.initPromise = null;
  }
}

// ============================================================
// Singleton Export
// ============================================================

export const merkleTreeWorkerService = new MerkleTreeWorkerService();
